import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import apiKeyManager from './apiKeyManager.js'

// Re-export shared utilities for backward compatibility
export {
  formatCurrency,
  safeDivide,
  formatPercent,
  parseAddressComponents as parseAddressComponentsShared,
  formatAddressForMap as formatAddressForMapShared,
  // [Client Name] / [Estimate Number] / [Total Amount] … in terms, intro, outro
  replaceMergeTags,
} from '@prosperia/shared'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Utility functions for the CRM application

/**
 * Generate a consistent color for a client's profile picture based on their name
 * Uses Tailwind CSS classes - web-specific
 * @param {string} name - The client's name
 * @returns {object} - Object containing background color and text color
 */
export const generateClientAvatarColors = (name) => {
  if (!name) {
    return {
      bgColor: 'bg-gray-500',
      textColor: 'text-white'
    }
  }

  // Predefined color combinations for consistent, attractive avatars
  const colorSchemes = [
    { bgColor: 'bg-blue-500', textColor: 'text-white' },
    { bgColor: 'bg-green-500', textColor: 'text-white' },
    { bgColor: 'bg-purple-500', textColor: 'text-white' },
    { bgColor: 'bg-pink-500', textColor: 'text-white' },
    { bgColor: 'bg-indigo-500', textColor: 'text-white' },
    { bgColor: 'bg-teal-500', textColor: 'text-white' },
    { bgColor: 'bg-orange-500', textColor: 'text-white' },
    { bgColor: 'bg-red-500', textColor: 'text-white' },
    { bgColor: 'bg-yellow-500', textColor: 'text-white' },
    { bgColor: 'bg-emerald-500', textColor: 'text-white' },
    { bgColor: 'bg-cyan-500', textColor: 'text-white' },
    { bgColor: 'bg-rose-500', textColor: 'text-white' },
    { bgColor: 'bg-violet-500', textColor: 'text-white' },
    { bgColor: 'bg-amber-500', textColor: 'text-white' },
    { bgColor: 'bg-lime-500', textColor: 'text-white' },
    { bgColor: 'bg-sky-500', textColor: 'text-white' }
  ]

  // Generate a hash from the name to consistently assign the same color to the same name
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Use the hash to select a color scheme
  const colorIndex = Math.abs(hash) % colorSchemes.length
  return colorSchemes[colorIndex]
}

/**
 * Generate initials from a client's name
 * @param {string} name - The client's full name
 * @returns {string} - Initials (e.g., "JD" for "John Doe")
 */
export const generateClientInitials = (name) => {
  if (!name) return '?'

  const nameParts = name.trim().split(' ')
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase()
  }

  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase()
}

// Geolocation and reverse geocoding helpers
/**
 * Get the user's current position using the Geolocation API
 * Returns { lat, lng }
 */
export const getCurrentLocation = (timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Location is not supported on this device'))
      return
    }

    let timeoutId = null
    const onSuccess = (pos) => {
      if (timeoutId) clearTimeout(timeoutId)
      const { latitude, longitude } = pos.coords || {}
      if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        reject(new Error('Invalid coordinates'))
        return
      }
      resolve({ lat: latitude, lng: longitude })
    }
    const onError = (err) => {
      if (timeoutId) clearTimeout(timeoutId)
      reject(err || new Error('Unable to fetch location'))
    }

    timeoutId = setTimeout(() => onError(new Error('Location request timed out')), timeoutMs)

    try {
      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0
      })
    } catch (err) {
      onError(err)
    }
  })
}

/**
 * Reverse geocode lat/lng to a human-friendly address using Google Maps JS API.
 * Falls back to a lat,lng string if API key is missing or lookup fails.
 */
export const reverseGeocodeLatLng = async (lat, lng) => {
  try {
    const apiKey = apiKeyManager.getGoogleMapsKey()
    if (!apiKey) {
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
    }
    
    // Use centralized Google Maps loader
    const { loadGoogleMaps } = await import('./googleMapsLoader')
    const google = await loadGoogleMaps()
    
    const geocoder = new google.maps.Geocoder()
    const result = await geocoder.geocode({ location: { lat, lng } })
    const address = result?.results?.[0]?.formatted_address
    return address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`
  } catch (error) {
    console.warn('reverseGeocodeLatLng failed; falling back to coordinates', error)
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
  }
}

/**
 * Convenience: get formatted address from the user's current location.
 */
export const getAddressFromCurrentLocation = async () => {
  const { lat, lng } = await getCurrentLocation()
  return await reverseGeocodeLatLng(lat, lng)
}

/**
 * Format address for map usage - prefer coordinates if available
 */
export const formatAddressForMap = (client) => {
  if (!client) return ''
  
  // If coordinates are available, use them for precise positioning
  if (client.coordinates) {
    try {
      const coords = typeof client.coordinates === 'string' 
        ? JSON.parse(client.coordinates) 
        : client.coordinates
      if (coords.lat && coords.lng) {
        return `${coords.lat},${coords.lng}`
      }
    } catch (error) {
      console.warn('Failed to parse coordinates:', error)
    }
  }
  
  // Fall back to formatted address
  const parts = [client.address, client.city, client.state, client.zip].filter(Boolean)
  return parts.join(', ')
}

/**
 * Parse address components from Google Places result
 */
export const parseAddressComponents = (place) => {
  if (!place || !place.address_components) {
    return {
      streetAddress: '',
      city: '',
      state: '',
      zip: '',
      coordinates: null
    }
  }

  const addressComponents = place.address_components
  let streetAddress = ''
  let city = ''
  let state = ''
  let zip = ''

  // Parse address components
  addressComponents.forEach(component => {
    const types = component.types
    if (types.includes('street_number') || types.includes('route')) {
      streetAddress += (streetAddress ? ' ' : '') + component.long_name
    } else if (types.includes('locality')) {
      city = component.long_name
    } else if (types.includes('administrative_area_level_1')) {
      state = component.short_name
    } else if (types.includes('postal_code')) {
      zip = component.long_name
    }
  })

  return {
    streetAddress,
    city,
    state,
    zip,
    coordinates: place.geometry ? {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    } : null
  }
}

/**
 * Replace automation tags in automation messages
 * Supports square bracket format like [Client Name], [Job Title], etc.
 * This is used for automation subject and body fields
 * @param {string} text - Text containing automation tags
 * @param {Object} data - Data object containing automation context
 * @param {Object} data.client - Client information (name, email, phone, address)
 * @param {Object} data.company - Company information (name, phone, email)
 * @param {Object} data.job - Job information (title, description, scheduledDateTime, etc.)
 * @param {Object} data.technician - Technician information (name, phone)
 * @param {Object} data.estimate - Estimate information (total, number)
 * @param {Object} data.invoice - Invoice information (number, total)
 * @returns {string} Text with tags replaced
 */
export const replaceAutomationTags = (text, data = {}) => {
  if (!text || typeof text !== 'string') {
    return text || ''
  }

  let processed = text

  // Get values with fallbacks
  const clientName = data.client?.name || data.clientName || 'Client'
  const clientEmail = data.client?.email || ''
  const clientPhone = data.client?.phone || ''
  const clientAddress = data.client?.address || data.client?.full_address || ''
  
  const companyName = data.company?.name || data.companyName || 'Company'
  const companyPhone = data.company?.phone || ''
  const companyEmail = data.company?.email || ''
  
  const jobTitle = data.job?.title || data.job?.name || 'Service'
  const jobDescription = data.job?.description || ''
  const jobScheduledDateTime = data.job?.scheduledDateTime || data.job?.scheduled_date_time || ''
  const jobEstimatedHours = data.job?.estimatedHours || data.job?.estimated_hours || ''
  const jobEstimatedArrival = data.job?.estimatedArrival || data.job?.estimated_arrival || ''
  
  const technicianName = data.technician?.name || 'Our team'
  const technicianPhone = data.technician?.phone || ''
  
  const estimateTotal = data.estimate?.total || data.estimateTotal || 0
  const estimateNumber = data.estimate?.number || data.estimateNumber || data.estimate?.estimate_number || ''
  
  const invoiceNumber = data.invoice?.number || data.invoiceNumber || data.invoice?.invoice_number || ''
  const invoiceTotal = data.invoice?.total || data.invoiceTotal || 0
  
  // Format amounts as currency
  const formattedEstimateTotal = typeof estimateTotal === 'number' 
    ? `$${estimateTotal.toFixed(2)}` 
    : (typeof estimateTotal === 'string' && estimateTotal.trim() ? estimateTotal : '$0.00')
  
  const formattedInvoiceTotal = typeof invoiceTotal === 'number' 
    ? `$${invoiceTotal.toFixed(2)}` 
    : (typeof invoiceTotal === 'string' && invoiceTotal.trim() ? invoiceTotal : '$0.00')

  // Format dates
  const currentDate = new Date().toLocaleDateString()
  const currentTime = new Date().toLocaleTimeString()

  // Define replacements - use square brackets like [Client Name]
  const replacements = [
    { placeholder: '[Client Name]', value: clientName },
    { placeholder: '[Client Email]', value: clientEmail },
    { placeholder: '[Client Phone]', value: clientPhone },
    { placeholder: '[Client Address]', value: clientAddress },
    { placeholder: '[Company Name]', value: companyName },
    { placeholder: '[Company Phone]', value: companyPhone },
    { placeholder: '[Company Email]', value: companyEmail },
    { placeholder: '[Job Title]', value: jobTitle },
    { placeholder: '[Job Description]', value: jobDescription },
    { placeholder: '[Job Scheduled Date]', value: jobScheduledDateTime },
    { placeholder: '[Job Estimated Hours]', value: String(jobEstimatedHours) },
    { placeholder: '[Job Estimated Arrival]', value: jobEstimatedArrival },
    { placeholder: '[Technician Name]', value: technicianName },
    { placeholder: '[Technician Phone]', value: technicianPhone },
    { placeholder: '[Estimate Number]', value: String(estimateNumber) },
    { placeholder: '[Estimate Total]', value: formattedEstimateTotal },
    { placeholder: '[Invoice Number]', value: String(invoiceNumber) },
    { placeholder: '[Invoice Total]', value: formattedInvoiceTotal },
    { placeholder: '[Date]', value: currentDate },
    { placeholder: '[Time]', value: currentTime }
  ]

  // Replace each placeholder using simple string split/join for reliability
  replacements.forEach(({ placeholder, value }) => {
    if (processed.includes(placeholder)) {
      processed = processed.split(placeholder).join(value)
    }
  })

  return processed
}